function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamProductPrice <iam-product-price>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Displays the product price in the product form.
// - It updates the price display whenever a variant is changed.
// - It uses the section rendering API to fetch the `product-form__price` section for the selected variant.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:variant:changed' → Listens for when the product variant is
//                                   changed to trigger updating the price
// ----------------------------------------------------------------------------
// Event Triggers:
// - 'productForm:price:updated'   → Triggered after the price is updated
// ----------------------------------------------------------------------------
// Event Filters:
// - 'productForm:price:updating'  → Allows other modules to modify the price HTML before updating
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Updates the product price by fetching the new price from the server and replacing
//   the components inner HTML with the new price content.
// ----------------------------------------------------------------------------
// Gotchas:
// - Relies on the AJAX call to fetch the price HTML, so any error in that request will
//   result in an error message (error_retrieving_price) being displayed.
// ----------------------------------------------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';
import { sectionRendering } from 'scripts/helpers/iamota-shopify-ajax';
import { getUrlWithVariant } from '@shopify/theme-product-form';
import translate from 'scripts/helpers/iamota-translate';
import registerCustomElement from 'scripts/helpers/register-custom-element';

// Styles
import './_product-price.scss';
import { promoPriceOverrides } from '../promo-price-override/promo-price-overrides';

// -------------------------------------
// Web Component
// -------------------------------------

class iamProductPrice extends iamFormElement {
  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Bind Event Listeners
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "product-price.js"
    });

    // Update the price to the inital value
    this.updatePrice(this.iamForm.getCurrentVariant());
  }

  /**
   * Trigger actions when the variant changes
   * @param {variantObject} variant
   */
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    this.updatePrice(variant);
  }

  /**
   * Update the price based on the specified variant.
   * @param {variantObject} variant
   */
  updatePrice(variant) {
    var _this = this;
    if (variant) {
      this.setLoading(true);
      sectionRendering.fetchSingle('main-product__price', getUrlWithVariant(window.location.pathname, variant.id)).then(/*#__PURE__*/function () {
        var _ref2 = _asyncToGenerator(function* (sectionContent) {
          var _promoPriceOverrides$;
          var priceHTML = yield iamotaEventHandler.applyFilters('productForm:price:updating', sectionContent, {
            context: _this.iamForm
          });
          var oneTimeDiscountPercent = (_promoPriceOverrides$ = promoPriceOverrides.oneTimePurchasePromo) === null || _promoPriceOverrides$ === void 0 ? void 0 : _promoPriceOverrides$.getDiscountPercent(variant.sku);
          if (oneTimeDiscountPercent) {
            var originalPrice = promoPriceOverrides.oneTimePurchasePromo.formatCentsToDollars(variant.price);
            var template = "<span class=\"visually-hidden\">".concat(originalPrice, "</span>\n                                            <s class=\"h4 price__sale\">").concat(originalPrice, "</s>\n                                            <span class=\"h4 price__original price__original--sale\" id=\"ProductPrice\">\n                                                ").concat(promoPriceOverrides.oneTimePurchasePromo.calculateDiscountedPrice(variant.price, oneTimeDiscountPercent), "\n                                            </span>\n                                            <span class=\"selling-plan__discount\">").concat(oneTimeDiscountPercent, "% OFF</span>\n                                         ");
            _this.innerHTML = template;
          } else {
            _this.innerHTML = priceHTML;
          }
          iamotaEventHandler.trigger('productForm:price:updated', {
            priceHTML
          }, {
            context: _this.iamForm
          });
        });
        return function (_x) {
          return _ref2.apply(this, arguments);
        };
      }()).catch(error => {
        console.error('productForm: Error fetching price section:', error);
        this.innerHTML = translate('An unexpected error occurrred when retrieving the price, please try refreshing the page', 'productForm.error_retrieving_price');
      }).finally(() => {
        this.setLoading(false);
      });
    } else {
      // No variant, hide the price
      this.innerHTML = '';
    }
  }
}
registerCustomElement('iam-product-price', iamProductPrice);